import { useCallback, useRef, useState } from "react";
import moment from "moment";

// nutrition.controller.js caps activeExercises/dayPersonalRecords/habitLogs
// to a trailing 1-year window on the initial SSR load (dailyStats/
// nutritionPlans have no such cap). This MUST match that controller's own
// `moment().subtract(1, "year")` — it's the boundary this hook assumes is
// already loaded before ever calling ensureRangeLoaded.
const INITIAL_LOADED_DAYS_BACK = 1;
const DATE_FMT = "YYYY-MM-DD";

const fetchJson = async (path, params) => {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
  ).toString();
  const res = await fetch(`/actions/${path}?${qs}`, { credentials: "same-origin" });
  if (!res.ok) throw new Error(`Failed to fetch /actions/${path}`);
  return res.json();
};

// Extends activeExercises/dayPersonalRecords/habitLogs beyond the SSR's
// trailing-1-year window, client-side, no page reload — for when the coach
// picks a range (quick-select "lifetime" or a manual date) that reaches
// further back than what the initial page load fetched. Hits the 3 GET
// routes added for this: js/actions/exercise.js's "/exercise/active-range",
// personal-records.js, habit-logs.js.
//
// activeExercises/habitLogs are flat, appendable arrays — extending only
// fetches the DELTA (the missing older slice) and concatenates it with what's
// already loaded. dayPersonalRecords is different: msb-api computes it by
// scanning the athlete's ENTIRE history and filtering server-side down to
// [start,end] (see personal-records.js's own investigation notes — the DB
// scan isn't even bounded by `start`), so there's no meaningful delta to
// merge — extending re-fetches the FULL window fresh and replaces the old
// value outright, rather than trying to combine two partially-computed sets.
export const useExtendedHistoryData = ({
  proxyAthlete,
  initialActiveExercises = [],
  initialDayPersonalRecords = {},
  initialHabitLogs = [],
}) => {
  const [activeExercises, setActiveExercises] = useState(initialActiveExercises);
  const [dayPersonalRecords, setDayPersonalRecords] = useState(initialDayPersonalRecords);
  const [habitLogs, setHabitLogs] = useState(initialHabitLogs);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // How far back real data is currently loaded — starts at the SSR's own
  // trailing-1-year boundary, moves earlier every time ensureRangeLoaded
  // successfully extends it. A ref (not state) since it's read/written
  // synchronously inside ensureRangeLoaded to dedupe overlapping calls,
  // not something a render needs to react to on its own.
  const loadedFromUtc = useRef(
    Number(moment().subtract(INITIAL_LOADED_DAYS_BACK, "years").startOf("day").format("YYYYMMDD"))
  );
  // Only one extension fetch runs at a time. A second call arriving while
  // one is in flight (rapid quick-select clicks, a manual date typed twice)
  // would otherwise race it with an OVERLAPPING delta range — both merges
  // land, and the overlap ends up duplicated in activeExercises/habitLogs.
  // Instead it just records the latest wanted range here; the in-flight
  // call picks it up and re-invokes itself once it finishes, so requests
  // are serialized and the latest one always eventually wins.
  const isFetchingRef = useRef(false);
  const pendingRef = useRef(null);

  // Call with the range the coach just selected — a no-op if it's already
  // fully covered by what's loaded. Wrapped in useCallback so its identity
  // only changes with proxyAthlete — callers can safely put it in a
  // useEffect's dependency array without refiring on every render.
  const ensureRangeLoaded = useCallback(
    async (dateFrom, dateTo) => {
      const wantedFromUtc = Number(moment(dateFrom).format("YYYYMMDD"));
      if (wantedFromUtc >= loadedFromUtc.current) return;

      if (isFetchingRef.current) {
        pendingRef.current = { dateFrom, dateTo };
        return;
      }

      isFetchingRef.current = true;
      setIsLoadingHistory(true);
      try {
        const newStart = moment(dateFrom).format(DATE_FMT);
        const deltaEnd = moment(String(loadedFromUtc.current), "YYYYMMDD")
          .subtract(1, "day")
          .format(DATE_FMT);
        const fullEnd = moment(dateTo).format(DATE_FMT);

        const [exercisesDelta, habitsDelta, prsFull] = await Promise.all([
          fetchJson("exercise/active-range", { start: newStart, end: deltaEnd, proxyAthlete }),
          fetchJson("habit-logs", { start: newStart, end: deltaEnd, proxyAthlete }),
          fetchJson("personal-records", {
            start: newStart,
            end: fullEnd,
            proxyAthlete,
            isCheckIn: true,
          }),
        ]);

        setActiveExercises((prev) => [
          ...(Array.isArray(exercisesDelta) ? exercisesDelta : []),
          ...prev,
        ]);
        setHabitLogs((prev) => [...(Array.isArray(habitsDelta) ? habitsDelta : []), ...prev]);
        setDayPersonalRecords(prsFull ?? {});
        loadedFromUtc.current = wantedFromUtc;
      } finally {
        isFetchingRef.current = false;
        setIsLoadingHistory(false);
        const pending = pendingRef.current;
        pendingRef.current = null;
        if (pending) ensureRangeLoaded(pending.dateFrom, pending.dateTo);
      }
    },
    [proxyAthlete]
  );

  return {
    activeExercises,
    dayPersonalRecords,
    habitLogs,
    isLoadingHistory,
    ensureRangeLoaded,
  };
};
